import { useEffect, useMemo, useRef, useState, type PointerEvent } from "react";
import { cn } from "./cn";
import { EmptyState } from "./feedback";

export interface ChartSeries {
  key: string;
  label: string;
  /** Categorical slot 1-3; identity follows the entity, never its rank. */
  slot: 1 | 2 | 3;
  values: number[];
}

const HEIGHT = 180;
const PAD = { top: 12, right: 64, bottom: 22, left: 44 };

function niceMax(v: number): number {
  if (v <= 0) return 1;
  const exp = 10 ** Math.floor(Math.log10(v));
  const f = v / exp;
  return (f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10) * exp;
}

/**
 * Time-series line chart: 2px lines, recessive grid, crosshair tooltip listing
 * every series, direct end labels, legend for 2+ series and a table view.
 */
export function LineChart({
  times,
  series,
  format,
  max,
  title,
  emptyText = "No data for this range yet",
}: {
  times: string[];
  series: ChartSeries[];
  format: (v: number) => string;
  /** Fixed y maximum (e.g. 100 for percentages); otherwise derived from the data. */
  max?: number;
  title: string;
  emptyText?: string;
}) {
  const wrap = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(600);
  const [hover, setHover] = useState<number | null>(null);
  const [table, setTable] = useState(false);

  useEffect(() => {
    const el = wrap.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setWidth(Math.max(240, Math.floor(e!.contentRect.width))));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const n = times.length;
  const yMax = useMemo(() => max ?? niceMax(Math.max(0, ...series.flatMap((s) => s.values)) * 1.1), [max, series]);
  const innerW = width - PAD.left - PAD.right;
  const innerH = HEIGHT - PAD.top - PAD.bottom;
  const x = (i: number) => PAD.left + (n <= 1 ? innerW / 2 : (i / (n - 1)) * innerW);
  const y = (v: number) => PAD.top + innerH - (Math.min(v, yMax) / yMax) * innerH;
  const ticks = [0, 0.5, 1].map((f) => f * yMax);
  const timeFmt = (iso: string) => {
    const d = new Date(iso);
    const span = n > 1 ? new Date(times[n - 1]!).getTime() - new Date(times[0]!).getTime() : 0;
    return span > 2 * 86400_000 ? d.toLocaleDateString(undefined, { month: "short", day: "numeric" }) : d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  };

  const onMove = (e: PointerEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const px = e.clientX - rect.left;
    if (n === 0) return;
    const i = Math.round(((px - PAD.left) / innerW) * (n - 1));
    setHover(Math.max(0, Math.min(n - 1, i)));
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[13px] font-medium">{title}</p>
        <div className="flex items-center gap-3">
          {series.length > 1 &&
            series.map((s) => (
              <span key={s.key} className="flex items-center gap-1.5 text-[12px] text-muted">
                <span className="h-0.5 w-3 rounded" style={{ background: `var(--hd-series-${s.slot})` }} />
                {s.label}
              </span>
            ))}
          <button type="button" className="text-[12px] text-subtle hover:text-fg" onClick={() => setTable(!table)}>
            {table ? "Chart" : "Table"}
          </button>
        </div>
      </div>
      <div ref={wrap} className="relative">
        {n === 0 ? (
          <EmptyState title={emptyText} />
        ) : table ? (
          <div className="max-h-[180px] overflow-auto rounded border border-line">
            <table className="w-full text-[12px]">
              <thead className="sticky top-0 bg-surface-2">
                <tr>
                  <th className="px-2 py-1 text-left font-medium text-muted">Time</th>
                  {series.map((s) => (
                    <th key={s.key} className="px-2 py-1 text-right font-medium text-muted">{s.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {times.map((t, i) => (
                  <tr key={t} className="border-t border-line">
                    <td className="px-2 py-1 text-muted tabular-nums">{new Date(t).toLocaleString()}</td>
                    {series.map((s) => (
                      <td key={s.key} className="px-2 py-1 text-right tabular-nums">{format(s.values[i] ?? 0)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <>
            <svg
              width={width}
              height={HEIGHT}
              role="img"
              aria-label={`${title} chart`}
              onPointerMove={onMove}
              onPointerLeave={() => setHover(null)}
              className="block touch-none"
            >
              {ticks.map((t) => (
                <g key={t}>
                  <line x1={PAD.left} x2={width - PAD.right} y1={y(t)} y2={y(t)} stroke="var(--hd-border)" strokeWidth={1} />
                  <text x={PAD.left - 6} y={y(t) + 3} textAnchor="end" fontSize={10.5} fill="var(--hd-fg-subtle)">
                    {format(t)}
                  </text>
                </g>
              ))}
              <text x={PAD.left} y={HEIGHT - 6} fontSize={10.5} fill="var(--hd-fg-subtle)">{timeFmt(times[0]!)}</text>
              <text x={width - PAD.right} y={HEIGHT - 6} fontSize={10.5} textAnchor="end" fill="var(--hd-fg-subtle)">{timeFmt(times[n - 1]!)}</text>
              {series.map((s) => (
                <polyline
                  key={s.key}
                  fill="none"
                  stroke={`var(--hd-series-${s.slot})`}
                  strokeWidth={2}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  points={s.values.map((v, i) => `${x(i)},${y(v)}`).join(" ")}
                />
              ))}
              {/* Direct end labels so identity never depends on color alone. */}
              {series.map((s) => {
                const last = s.values[s.values.length - 1] ?? 0;
                return (
                  <text key={`l-${s.key}`} x={x(n - 1) + 6} y={y(last) + 3} fontSize={10.5} fill="var(--hd-fg-muted)">
                    {series.length > 1 ? s.label : format(last)}
                  </text>
                );
              })}
              {hover !== null && (
                <g>
                  <line x1={x(hover)} x2={x(hover)} y1={PAD.top} y2={PAD.top + innerH} stroke="var(--hd-fg-subtle)" strokeWidth={1} />
                  {series.map((s) => (
                    <circle key={s.key} cx={x(hover)} cy={y(s.values[hover] ?? 0)} r={4} fill={`var(--hd-series-${s.slot})`} stroke="var(--hd-surface)" strokeWidth={2} />
                  ))}
                </g>
              )}
            </svg>
            {hover !== null && (
              <div
                className={cn("pointer-events-none absolute top-1 z-10 rounded-md border border-line bg-surface px-2.5 py-1.5 text-[12px] shadow-lg")}
                style={x(hover) > width / 2 ? { right: width - x(hover) + 10 } : { left: x(hover) + 10 }}
              >
                <p className="mb-0.5 text-subtle">{new Date(times[hover]!).toLocaleString()}</p>
                {series.map((s) => (
                  <p key={s.key} className="flex items-center gap-1.5 whitespace-nowrap">
                    <span className="h-2 w-2 rounded-full" style={{ background: `var(--hd-series-${s.slot})` }} />
                    <strong className="tabular-nums">{format(s.values[hover] ?? 0)}</strong>
                    <span className="text-muted">{s.label}</span>
                  </p>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export function formatBytes(n: number | null | undefined): string {
  if (n === null || n === undefined) return "—";
  const u = ["B", "KB", "MB", "GB", "TB", "PB"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < u.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v >= 100 || i === 0 ? v.toFixed(0) : v.toFixed(1)} ${u[i]}`;
}

export const formatRate = (bps: number) => `${formatBytes(bps)}/s`;

export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return "—";
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return d > 0 ? `${d}d ${h}h` : h > 0 ? `${h}h ${m}m` : `${m}m`;
}
